import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, FileText, Image, Mic, Subtitles, Package, Check, ChevronRight, ChevronLeft,
  RefreshCw, Copy, Save, Send, Clock, Loader2, Coins, AlertTriangle, Cpu, Eye,
  Pencil, Wand2, ArrowRight, ArrowLeft, RotateCcw, Download, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════ STEP DEFINITIONS ═══════════════════ */

const STEPS = [
  { id: 1, label: 'İçerik Türü', short: 'Tür' },
  { id: 2, label: 'Platform', short: 'Platform' },
  { id: 3, label: 'AI Modelleri', short: 'AI' },
  { id: 4, label: 'Detaylar', short: 'Detay' },
  { id: 5, label: 'Üretim & Düzenleme', short: 'Sonuç' },
  { id: 6, label: 'Önizleme', short: 'Önizle' },
  { id: 7, label: 'Paylaşım', short: 'Paylaş' },
];

const CONTENT_MODULES = [
  { id: 'text_generation', label: 'Metin', desc: 'Başlık, açıklama, CTA', icon: FileText, color: 'violet' },
  { id: 'image_generation', label: 'Görsel', desc: 'AI görsel üretimi', icon: Image, color: 'blue' },
  { id: 'audio_generation', label: 'Seslendirme', desc: 'Metinden ses', icon: Mic, color: 'green' },
  { id: 'subtitle_generation_text', label: 'Altyazı', desc: 'Otomatik altyazı', icon: Subtitles, color: 'yellow' },
];

const QUICK_PACKS = [
  { id: 'text_only', label: 'Sadece Metin', modules: ['text_generation'] },
  { id: 'text_image', label: 'Metin + Görsel', modules: ['text_generation', 'image_generation'] },
  { id: 'full_social', label: 'Sosyal Medya Paketi', modules: ['text_generation', 'image_generation', 'audio_generation'] },
  { id: 'full_pack', label: 'Tam Paket', modules: ['text_generation', 'image_generation', 'audio_generation', 'subtitle_generation_text'] },
];

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: '#E4405F' },
  { id: 'facebook', label: 'Facebook', color: '#1877F2' },
  { id: 'twitter', label: 'X (Twitter)', color: '#000' },
  { id: 'telegram', label: 'Telegram', color: '#0088CC' },
  { id: 'tiktok', label: 'TikTok', color: '#010101' },
  { id: 'youtube', label: 'YouTube', color: '#FF0000' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { id: 'genel', label: 'Genel', color: '#737373' },
];

const CONTENT_TYPES = [
  { value: 'caption', label: 'Sosyal Medya Başlığı' },
  { value: 'hashtags', label: 'Hashtagler' },
  { value: 'blog_outline', label: 'Blog Taslağı' },
  { value: 'ad_copy', label: 'Reklam Metni' },
  { value: 'short_script', label: 'Kısa Senaryo' },
];

const TONES = ['Profesyonel', 'Günlük', 'Esprili', 'İlham Verici', 'Eğitici', 'Cesur', 'Samimi'];

const colorMap = {
  violet: 'from-violet-500/20 to-violet-600/5 border-violet-500/30 text-violet-400',
  blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400',
  green: 'from-green-500/20 to-green-600/5 border-green-500/30 text-green-400',
  yellow: 'from-yellow-500/20 to-yellow-600/5 border-yellow-500/30 text-yellow-400',
};

/* ═══════════════════ PROGRESS STEPPER ═══════════════════ */

function Stepper({ currentStep, steps, onStepClick }) {
  return (
    <div className="flex items-center gap-1 w-full overflow-x-auto pb-2" data-testid="wizard-stepper">
      {steps.map((step, i) => {
        const isActive = currentStep === step.id;
        const isDone = currentStep > step.id;
        return (
          <div key={step.id} className="flex items-center flex-shrink-0">
            <button
              onClick={() => isDone && onStepClick(step.id)}
              disabled={!isDone}
              data-testid={`step-${step.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-ibm transition-all ${
                isActive
                  ? 'bg-violet-600/20 border border-violet-500/30 text-violet-300'
                  : isDone
                    ? 'bg-green-500/10 border border-green-500/20 text-green-400 cursor-pointer hover:bg-green-500/20'
                    : 'bg-[#141414] border border-[#1A1A1A] text-[#525252]'
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                isActive ? 'bg-violet-600 text-white' : isDone ? 'bg-green-500 text-white' : 'bg-[#262626] text-[#525252]'
              }`}>
                {isDone ? <Check size={10} /> : step.id}
              </div>
              <span className="hidden sm:inline">{step.short}</span>
            </button>
            {i < steps.length - 1 && <ChevronRight size={12} className="text-[#262626] mx-0.5 flex-shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════ STEP 1: CONTENT TYPE ═══════════════════ */

function Step1({ selectedModules, setSelectedModules }) {
  const toggleModule = (id) => {
    setSelectedModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };
  const applyPack = (pack) => setSelectedModules([...pack.modules]);

  return (
    <div className="space-y-6" data-testid="step-content-type">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Ne üretmek istiyorsunuz?</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Üretilecek içerik modüllerini seçin</p>
      </div>

      {/* Quick Packs */}
      <div>
        <p className="text-xs text-[#525252] font-ibm mb-2 uppercase tracking-wider">Hızlı Paketler</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_PACKS.map(pack => {
            const isActive = pack.modules.every(m => selectedModules.includes(m)) && selectedModules.length === pack.modules.length;
            return (
              <button key={pack.id} onClick={() => applyPack(pack)} data-testid={`pack-${pack.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-ibm border transition-all ${
                  isActive ? 'border-violet-500/40 bg-violet-500/10 text-violet-300' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3] hover:border-[#404040]'
                }`}>
                <Package size={12} /> {pack.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Module Cards */}
      <div className="grid grid-cols-2 gap-3">
        {CONTENT_MODULES.map(mod => {
          const selected = selectedModules.includes(mod.id);
          return (
            <button key={mod.id} onClick={() => toggleModule(mod.id)} data-testid={`module-${mod.id}`}
              className={`relative p-5 rounded-xl border text-left transition-all ${
                selected
                  ? `bg-gradient-to-br ${colorMap[mod.color]} border`
                  : 'bg-[#0F0F0F] border-[#1A1A1A] hover:border-[#333] text-[#A3A3A3]'
              }`}>
              {selected && <div className="absolute top-3 right-3"><Check size={14} className="text-green-400" /></div>}
              <mod.icon size={24} className={selected ? '' : 'text-[#525252]'} />
              <p className={`font-ibm font-medium text-sm mt-3 ${selected ? 'text-white' : ''}`}>{mod.label}</p>
              <p className="text-xs text-[#525252] mt-1">{mod.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════ STEP 2: PLATFORM ═══════════════════ */

function Step2({ selectedPlatforms, setSelectedPlatforms }) {
  const toggle = (id) => {
    setSelectedPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };
  const selectAll = () => setSelectedPlatforms(PLATFORMS.map(p => p.id));

  return (
    <div className="space-y-6" data-testid="step-platform">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Hangi platformlarda paylaşacaksınız?</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Birden fazla platform seçebilirsiniz</p>
      </div>
      <Button variant="outline" size="sm" onClick={selectAll} className="border-[#262626] text-[#A3A3A3] hover:text-white text-xs h-7">
        Hepsini Seç
      </Button>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {PLATFORMS.map(p => {
          const selected = selectedPlatforms.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggle(p.id)} data-testid={`platform-${p.id}`}
              className={`p-4 rounded-xl border text-center transition-all ${
                selected ? 'bg-[#141414] border-violet-500/30' : 'bg-[#0A0A0A] border-[#1A1A1A] hover:border-[#333]'
              }`}>
              <div className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center font-outfit font-bold text-sm"
                style={{ background: `${p.color}20`, color: p.color }}>
                {p.label[0]}
              </div>
              <p className={`text-xs font-ibm ${selected ? 'text-white' : 'text-[#737373]'}`}>{p.label}</p>
              {selected && <Check size={12} className="mx-auto mt-1 text-green-400" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════ STEP 3: AI MODELS ═══════════════════ */

function Step3({ selectedModules, models, setModels, providers }) {
  const preferEmergent = (options) => {
    const emergent = options.find(o => !o.id?.includes('direct'));
    return emergent?.id || options[0]?.id || '';
  };

  const modelOptions = {
    text_generation: providers?.text?.models || [{ id: 'openai/gpt-5.2', label: 'GPT-5.2' }],
    image_generation: providers?.image?.models || [{ id: 'openai/gpt-image-1', label: 'GPT Image 1' }],
    audio_generation: [{ id: 'tts-1', label: 'OpenAI TTS-1' }, { id: 'tts-1-hd', label: 'OpenAI TTS-1 HD' }],
    subtitle_generation_text: [{ id: 'auto', label: 'Otomatik' }],
  };

  return (
    <div className="space-y-6" data-testid="step-ai-models">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Hangi AI modellerini kullanmak istiyorsunuz?</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Her modül için ayrı model seçebilirsiniz</p>
      </div>
      <div className="space-y-4">
        {selectedModules.map(modId => {
          const mod = CONTENT_MODULES.find(m => m.id === modId);
          const options = modelOptions[modId] || [];
          const defaultVal = preferEmergent(options);
          return (
            <Card key={modId} className="bg-[#141414] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorMap[mod.color]} flex items-center justify-center`}>
                    <mod.icon size={16} />
                  </div>
                  <div>
                    <p className="text-sm text-white font-ibm font-medium">{mod.label}</p>
                    <p className="text-[10px] text-[#525252]">{mod.desc}</p>
                  </div>
                </div>
                <Select value={models[modId] || defaultVal} onValueChange={v => setModels(prev => ({ ...prev, [modId]: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white h-9 text-sm" data-testid={`model-select-${modId}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {options.map(o => <SelectItem key={o.id || o.model} value={o.id || o.model}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════ STEP 4: DETAILS ═══════════════════ */

function Step4({ form, setForm, selectedModules, estimate }) {
  return (
    <div className="space-y-6" data-testid="step-details">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">İçerik detaylarını girin</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">Ne üretmek istediğinizi anlatın</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">İçerik Türü</Label>
          <Select value={form.content_type} onValueChange={v => setForm(p => ({ ...p, content_type: v }))}>
            <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="detail-content-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              {CONTENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">Konu / Hedef *</Label>
          <Textarea value={form.goal} onChange={e => setForm(p => ({ ...p, goal: e.target.value }))}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[80px]" data-testid="detail-goal"
            placeholder="Ne hakkında içerik üretmek istiyorsunuz? Detaylı yazın..." />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Ton</Label>
            <Select value={form.tone} onValueChange={v => setForm(p => ({ ...p, tone: v }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="detail-tone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {TONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#A3A3A3] font-ibm text-sm">Dil</Label>
            <Input value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="detail-language" />
          </div>
        </div>

        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">Hedef Kitle</Label>
          <Input value={form.target_audience || ''} onChange={e => setForm(p => ({ ...p, target_audience: e.target.value }))}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="detail-audience" placeholder="ör. 25-35 yaş, teknoloji meraklıları" />
        </div>

        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">CTA (Harekete Geçirici Mesaj)</Label>
          <Input value={form.cta || ''} onChange={e => setForm(p => ({ ...p, cta: e.target.value }))}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="detail-cta" placeholder="ör. Hemen deneyin, Linke tıklayın" />
        </div>

        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">Yasak Kelimeler</Label>
          <Input value={form.forbidden_words || ''} onChange={e => setForm(p => ({ ...p, forbidden_words: e.target.value }))}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="detail-forbidden" placeholder="Virgülle ayırın" />
        </div>

        <div>
          <Label className="text-[#A3A3A3] font-ibm text-sm">Ek Talimatlar</Label>
          <Textarea value={form.custom_instructions} onChange={e => setForm(p => ({ ...p, custom_instructions: e.target.value }))}
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[60px]" data-testid="detail-instructions"
            placeholder="AI'ya özel talimatlar..." />
        </div>
      </div>

      {/* Credit Estimate */}
      {estimate && (
        <Card className={`border ${estimate.sufficient ? 'bg-[#141414] border-[#262626]' : 'bg-red-500/5 border-red-500/20'}`} data-testid="credit-estimate">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#A3A3A3] font-ibm flex items-center gap-1.5"><Coins size={12} className="text-[#FACC15]" /> Tahmini Maliyet</span>
              <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 font-jetbrains">{estimate.estimated_cost} kredi</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#525252] font-ibm">Bakiye: {estimate.available_credits}</span>
              <span className={`text-xs font-jetbrains font-bold ${estimate.sufficient ? 'text-green-400' : 'text-red-400'}`}>
                {estimate.sufficient ? `Kalan: ${estimate.remaining_after}` : 'Yetersiz bakiye'}
              </span>
            </div>
            {!estimate.sufficient && (
              <div className="flex items-center gap-1.5 mt-2 text-red-400">
                <AlertTriangle size={12} /><span className="text-xs font-ibm">Yetersiz kredi bakiyesi</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════ STEP 5: GENERATION RESULT ═══════════════════ */

function Step5({ generating, jobs, result, setResult, mediaResults, onRegenerate, backendUrl }) {
  const [editingText, setEditingText] = useState(false);

  if (generating) {
    return (
      <div className="space-y-6" data-testid="step-generating">
        <div>
          <h2 className="font-outfit text-xl font-bold text-white">İçerik üretiliyor...</h2>
          <p className="text-sm text-[#737373] font-ibm mt-1">Lütfen bekleyin, AI çalışıyor</p>
        </div>
        <div className="space-y-3">
          {jobs.map(job => (
            <div key={job.id} className="p-4 bg-[#141414] rounded-xl border border-[#262626]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-violet-400" />
                  <span className="text-sm text-[#A3A3A3] font-ibm capitalize">{(job.type || '').replace(/_/g, ' ')}</span>
                </div>
                <Badge variant="outline" className="text-[10px] text-violet-400 border-violet-500/20">
                  {job.status === 'processing' ? 'Üretiliyor' : 'Sırada'}
                </Badge>
              </div>
              <Progress value={job.progress || 10} className="h-1.5" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!result && Object.keys(mediaResults).length === 0) {
    return (
      <div className="text-center py-16" data-testid="step-result-empty">
        <Sparkles size={48} className="mx-auto text-[#262626] mb-4" />
        <p className="text-[#737373] font-ibm text-sm">Üretim başlatılıyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-result">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit text-xl font-bold text-white">Sonucu gözden geçirin</h2>
          <p className="text-sm text-[#737373] font-ibm mt-1">Düzenleyin, revize edin veya yeniden üretin</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRegenerate} className="border-[#262626] text-[#A3A3A3] hover:text-white text-xs h-8" data-testid="regenerate-all-btn">
          <RefreshCw size={12} className="mr-1" /> Tümünü Yeniden Üret
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel: Visual */}
        {mediaResults.image_generation && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs text-[#525252] font-ibm uppercase tracking-wider">Görsel</h3>
              <Button variant="ghost" size="sm" onClick={onRegenerate} className="text-violet-400 hover:text-violet-300 h-6 text-[10px]" data-testid="regenerate-image-btn">
                <Wand2 size={10} className="mr-1" /> Yeniden Üret
              </Button>
            </div>
            <div className="rounded-xl border border-[#262626] overflow-hidden bg-[#0A0A0A]">
              <img src={`${backendUrl}${mediaResults.image_generation.url}`} alt="AI görsel" className="w-full object-contain max-h-[400px]" data-testid="result-image" />
            </div>
            <p className="text-[10px] text-[#525252] font-jetbrains">Model: {mediaResults.image_generation.model_used || 'gpt-image-1'}</p>
          </div>
        )}

        {/* Right Panel: Text */}
        {result && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs text-[#525252] font-ibm uppercase tracking-wider">Metin</h3>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditingText(!editingText)} className="text-blue-400 hover:text-blue-300 h-6 text-[10px]" data-testid="edit-text-btn">
                  <Pencil size={10} className="mr-1" /> {editingText ? 'Önizle' : 'Düzenle'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(result); toast.success('Kopyalandı'); }} className="text-[#737373] hover:text-white h-6 text-[10px]">
                  <Copy size={10} className="mr-1" /> Kopyala
                </Button>
              </div>
            </div>
            <div className="rounded-xl border border-[#262626] bg-[#0A0A0A] p-4 min-h-[200px]">
              {editingText ? (
                <textarea value={result} onChange={e => setResult(e.target.value)}
                  className="w-full bg-transparent text-sm text-white font-ibm leading-relaxed min-h-[300px] focus:outline-none resize-none" data-testid="edit-text-area" />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-[#E5E5E5] font-ibm leading-relaxed" data-testid="result-text">{result}</pre>
              )}
            </div>

            {/* Quick Revision Buttons */}
            <div>
              <p className="text-[10px] text-[#525252] font-ibm mb-1.5 uppercase tracking-wider">Hızlı Revizyon</p>
              <div className="flex flex-wrap gap-1.5">
                {['Kısalt', 'Uzat', 'Daha premium', 'Daha satışçı', 'Daha samimi', 'Daha profesyonel'].map(action => (
                  <button key={action} className="px-2.5 py-1 rounded-md bg-[#141414] border border-[#262626] text-[10px] text-[#A3A3A3] hover:text-white hover:border-violet-500/30 transition-all font-ibm">
                    {action}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Audio Result */}
      {mediaResults.audio_generation && (
        <div className="space-y-2">
          <h3 className="text-xs text-[#525252] font-ibm uppercase tracking-wider">Seslendirme</h3>
          <div className="p-3 bg-[#0A0A0A] rounded-xl border border-[#262626]">
            <audio controls className="w-full" data-testid="result-audio">
              <source src={`${backendUrl}${mediaResults.audio_generation.url}`} type="audio/mp3" />
            </audio>
            <p className="text-[10px] text-[#525252] font-jetbrains mt-1">Ses: {mediaResults.audio_generation.voice} | Model: {mediaResults.audio_generation.model_used || 'tts-1'}</p>
          </div>
        </div>
      )}

      {/* Subtitle Result */}
      {(mediaResults.subtitle_generation_text || mediaResults.subtitle_generation_stt) && (
        <div className="space-y-2">
          <h3 className="text-xs text-[#525252] font-ibm uppercase tracking-wider">Altyazı</h3>
          <pre className="text-xs text-[#A3A3A3] font-jetbrains bg-[#0A0A0A] p-3 rounded-xl border border-[#262626] max-h-[150px] overflow-y-auto whitespace-pre-wrap" data-testid="result-subtitle">
            {(mediaResults.subtitle_generation_text || mediaResults.subtitle_generation_stt)?.srt_content || ''}
          </pre>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ STEP 6: PREVIEW ═══════════════════ */

function Step6({ result, mediaResults, selectedPlatforms, backendUrl }) {
  const [activePreview, setActivePreview] = useState(selectedPlatforms[0] || 'instagram');

  return (
    <div className="space-y-6" data-testid="step-preview">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Platform Önizleme</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">İçeriğinizin her platformda nasıl görüneceğini kontrol edin</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {selectedPlatforms.map(pid => {
          const p = PLATFORMS.find(x => x.id === pid);
          if (!p) return null;
          return (
            <button key={pid} onClick={() => setActivePreview(pid)} data-testid={`preview-tab-${pid}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-ibm border transition-all ${
                activePreview === pid ? 'border-violet-500/30 bg-violet-500/10 text-white' : 'border-[#262626] text-[#737373] hover:text-white'
              }`}>
              {p.label}
            </button>
          );
        })}
      </div>

      <Card className="bg-[#0A0A0A] border-[#262626]">
        <CardContent className="p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-xs font-bold text-violet-400">S</div>
              <div>
                <p className="text-xs text-white font-ibm font-medium">Sosyal.Team</p>
                <p className="text-[10px] text-[#525252]">@sosyal.team</p>
              </div>
            </div>
            {mediaResults.image_generation && (
              <img src={`${backendUrl}${mediaResults.image_generation.url}`} alt="" className="w-full rounded-lg mb-3 border border-[#1A1A1A]" />
            )}
            {result && (
              <p className="text-sm text-[#E5E5E5] font-ibm leading-relaxed whitespace-pre-wrap">
                {activePreview === 'twitter' ? result.slice(0, 280) : result}
              </p>
            )}
            <p className="text-[10px] text-[#525252] mt-3 font-ibm">
              {activePreview === 'twitter' ? 'X — 280 karakter limiti' :
               activePreview === 'instagram' ? 'Instagram — Gönderi önizlemesi' :
               activePreview === 'telegram' ? 'Telegram — Mesaj önizlemesi' :
               `${PLATFORMS.find(p => p.id === activePreview)?.label || ''} önizlemesi`}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════ STEP 7: PUBLISH ═══════════════════ */

function Step7({ result, mediaResults, selectedPlatforms, onSave, onPublish }) {
  return (
    <div className="space-y-6" data-testid="step-publish">
      <div>
        <h2 className="font-outfit text-xl font-bold text-white">Paylaşım ve Kayıt</h2>
        <p className="text-sm text-[#737373] font-ibm mt-1">İçeriğinizi kaydedin veya sosyal medyada paylaşın</p>
      </div>

      <div className="space-y-3">
        <Card className="bg-[#141414] border-[#262626] hover:border-violet-500/20 transition-all cursor-pointer" onClick={onPublish} data-testid="publish-now-btn">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center"><Send size={22} className="text-violet-400" /></div>
            <div>
              <p className="text-white font-ibm font-medium text-sm">Hemen Paylaş</p>
              <p className="text-xs text-[#525252] mt-0.5">{selectedPlatforms.length} platforma anında gönder</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626] hover:border-blue-500/20 transition-all cursor-pointer" data-testid="schedule-btn">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center"><Calendar size={22} className="text-blue-400" /></div>
            <div>
              <p className="text-white font-ibm font-medium text-sm">Zamanla</p>
              <p className="text-xs text-[#525252] mt-0.5">İleri bir tarih ve saat seçerek zamanlayın</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626] hover:border-green-500/20 transition-all cursor-pointer" onClick={onSave} data-testid="save-library-btn">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center"><Save size={22} className="text-green-400" /></div>
            <div>
              <p className="text-white font-ibm font-medium text-sm">Medya Kütüphanesine Kaydet</p>
              <p className="text-xs text-[#525252] mt-0.5">İçeriği kaydedin, daha sonra paylaşın</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626] hover:border-yellow-500/20 transition-all cursor-pointer" data-testid="save-draft-btn">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center"><FileText size={22} className="text-yellow-400" /></div>
            <div>
              <p className="text-white font-ibm font-medium text-sm">Taslak Kaydet</p>
              <p className="text-xs text-[#525252] mt-0.5">Üzerinde çalışmaya devam edin</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════════ MAIN CONTENT STUDIO ═══════════════════ */

export default function ContentCreatorPage() {
  const [step, setStep] = useState(1);
  const [selectedModules, setSelectedModules] = useState(['text_generation']);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['instagram']);
  const [models, setModels] = useState({});
  const [providers, setProviders] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState({
    content_type: 'caption', goal: '', tone: 'Profesyonel', language: 'Türkçe',
    custom_instructions: '', target_audience: '', cta: '', forbidden_words: '',
  });

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [currentJobs, setCurrentJobs] = useState([]);
  const [result, setResult] = useState('');
  const [mediaResults, setMediaResults] = useState({});
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  // Load providers
  useEffect(() => { api.get('/providers').then(r => setProviders(r.data)).catch(() => {}); }, []);

  // Estimate credits when modules change
  useEffect(() => {
    if (selectedModules.length === 0) { setEstimate(null); return; }
    api.post('/credits/estimate', { generation_types: selectedModules })
      .then(r => setEstimate(r.data)).catch(() => setEstimate(null));
  }, [selectedModules]);

  const pollJobs = useCallback((jobIds) => {
    pollRef.current = setInterval(async () => {
      try {
        const results = await Promise.all(jobIds.map(id => api.get(`/jobs/${id}`)));
        const jobData = results.map(r => r.data);
        setCurrentJobs(jobData);
        const allDone = jobData.every(j => j.status === 'done' || j.status === 'failed');
        if (allDone) {
          stopPolling();
          setGenerating(false);
          const media = {};
          jobData.forEach(j => {
            if (j.status !== 'done') return;
            const gtype = j.type || j.payload?.generation_type;
            if (gtype === 'text_generation') {
              setResult(j.result || '');
            } else if (j.result) {
              try { media[gtype] = JSON.parse(j.result); } catch { media[gtype] = { raw: j.result }; }
            }
          });
          setMediaResults(media);
          const done = jobData.filter(j => j.status === 'done').length;
          if (done > 0) toast.success(`${done} üretim tamamlandı`);
          const failed = jobData.filter(j => j.status === 'failed');
          if (failed.length > 0) toast.error(failed[0].error_message || 'Bazı üretimler başarısız');
        }
      } catch { stopPolling(); setGenerating(false); }
    }, 2000);
  }, []);

  const handleGenerate = async () => {
    if (!form.goal) { toast.error('Lütfen bir konu/hedef girin'); return; }
    if (estimate && !estimate.sufficient) { toast.error('Yetersiz kredi bakiyesi'); return; }

    setGenerating(true);
    setResult('');
    setMediaResults({});
    setCurrentJobs([]);
    setStep(5);

    try {
      const textModel = models.text_generation || 'openai/gpt-5.2';
      const [text_provider, text_model] = textModel.includes('/') ? textModel.split('/') : ['openai', textModel];
      const imageModelFull = models.image_generation || 'openai/gpt-image-1';
      const image_model = imageModelFull.includes('/') ? imageModelFull.split('/').slice(1).join('/') : imageModelFull;
      const image_provider = imageModelFull.includes('/') ? imageModelFull.split('/')[0] : 'openai';
      const payload = {
        ...form, generation_types: selectedModules,
        platform: selectedPlatforms[0] || 'genel',
        text_provider, text_model,
        image_model, image_provider,
      };
      const res = await api.post('/content/generate', payload);
      const jobIds = res.data.jobs.map(j => j.id);
      setCurrentJobs(res.data.jobs);
      pollJobs(jobIds);
    } catch (err) {
      setGenerating(false);
      toast.error(err.response?.data?.detail || 'Üretim başarısız');
    }
  };

  const handleSave = async () => {
    if (!result) { toast.error('Kaydedilecek içerik yok'); return; }
    try {
      await api.post('/content/library', {
        title: `${form.content_type} - ${form.goal?.slice(0, 50)}`,
        content: result, type: form.content_type,
        metadata: { platform: selectedPlatforms[0], tone: form.tone, language: form.language },
      });
      toast.success('Medya kütüphanesine kaydedildi');
    } catch { toast.error('Kaydetme başarısız'); }
  };

  const handlePublish = async () => {
    if (!result) { toast.error('Paylaşılacak içerik yok'); return; }
    try {
      for (const platform of selectedPlatforms) {
        if (['telegram', 'twitter', 'instagram', 'facebook', 'tiktok', 'youtube'].includes(platform)) {
          await api.post('/sports/social/post', {
            content: result, platform,
            image_url: mediaResults.image_generation?.url || null,
          });
        }
      }
      toast.success(`${selectedPlatforms.length} platforma gönderildi`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Paylaşım hatası'); }
  };

  const canProceed = () => {
    if (step === 1) return selectedModules.length > 0;
    if (step === 2) return selectedPlatforms.length > 0;
    if (step === 3) return true;
    if (step === 4) return form.goal.trim().length > 0;
    if (step === 5) return (result || Object.keys(mediaResults).length > 0) && !generating;
    if (step === 6) return true;
    return true;
  };

  const nextStep = () => {
    if (step === 4) { handleGenerate(); return; }
    if (step < 7) setStep(step + 1);
  };
  const prevStep = () => { if (step > 1) setStep(step - 1); };

  return (
    <div data-testid="content-creator-page" className="animate-fade-in">
      {/* Header */}
      <div className="pb-5 mb-5 border-b border-[#262626]">
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/20 flex items-center justify-center">
            <Sparkles size={20} className="text-violet-400" />
          </div>
          İçerik Stüdyosu
        </h1>
        <p className="text-[#737373] font-ibm text-sm mt-1 ml-[52px]">Adım adım profesyonel AI içerik üretin</p>
      </div>

      {/* Stepper */}
      <Stepper currentStep={step} steps={STEPS} onStepClick={setStep} />

      {/* Step Content */}
      <div className="mt-6 min-h-[400px]">
        {step === 1 && <Step1 selectedModules={selectedModules} setSelectedModules={setSelectedModules} />}
        {step === 2 && <Step2 selectedPlatforms={selectedPlatforms} setSelectedPlatforms={setSelectedPlatforms} />}
        {step === 3 && <Step3 selectedModules={selectedModules} models={models} setModels={setModels} providers={providers} />}
        {step === 4 && <Step4 form={form} setForm={setForm} selectedModules={selectedModules} estimate={estimate} />}
        {step === 5 && <Step5 generating={generating} jobs={currentJobs} result={result} setResult={setResult} mediaResults={mediaResults} onRegenerate={handleGenerate} backendUrl={API_URL} />}
        {step === 6 && <Step6 result={result} mediaResults={mediaResults} selectedPlatforms={selectedPlatforms} backendUrl={API_URL} />}
        {step === 7 && <Step7 result={result} mediaResults={mediaResults} selectedPlatforms={selectedPlatforms} onSave={handleSave} onPublish={handlePublish} />}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-5 border-t border-[#262626]">
        <Button variant="outline" onClick={prevStep} disabled={step === 1}
          className="border-[#262626] text-[#A3A3A3] hover:text-white h-10" data-testid="prev-step-btn">
          <ArrowLeft size={14} className="mr-1.5" /> Geri
        </Button>

        <div className="flex items-center gap-2 text-xs text-[#525252] font-ibm">
          Adım {step} / {STEPS.length}
        </div>

        {step < 7 ? (
          <Button onClick={nextStep} disabled={!canProceed()}
            className="bg-violet-600 hover:bg-violet-500 text-white h-10 px-6" data-testid="next-step-btn">
            {step === 4 ? (
              <>{generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />} Üret ve Devam Et</>
            ) : (
              <>Devam <ArrowRight size={14} className="ml-1.5" /></>
            )}
          </Button>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}
